import { useMemo } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ReadingStepComponentProps } from "./types";
import { parseInstructionHtml } from "../utils";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

export function InstructionsStep({
  assessment,
  isLoading,
}: ReadingStepComponentProps) {
  const renderedInstructions = useMemo(
    () => parseInstructionHtml(assessment?.overallInstructions ?? ""),
    [assessment?.overallInstructions],
  );

  if (isLoading) {
    return null;
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      minWidth={"100%"}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {assessment?.instructionImage ? (
          <Box
            component="img"
            src={assessment.instructionImage}
            alt="Reading test instructions"
            onError={(event) => {
              const target = event.currentTarget as HTMLImageElement;
              target.style.display = "none";
            }}
            sx={{ height: "100%", width: "100%", objectFit: "contain" }}
          />
        ) : null}
      </Card>
      <Card
        variant={"filled"}
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Stack spacing={2} alignItems={"flex-start"} sx={{ minWidth: 0 }}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <InstructionAudioButton
                audioUrl={assessment?.instructionsAudio}
                label={`Play reading instruction audio`}
              ></InstructionAudioButton>
              <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                Instructions:
              </Typography>
            </Stack>
            {renderedInstructions ? (
              <Typography
                variant={"body1"}
                sx={{ whiteSpace: "pre-line", lineHeight: 1.2, minWidth: 0 }}
              >
                {renderedInstructions}
              </Typography>
            ) : (
              <Stack direction={"column"} spacing={2}>
                <Typography>
                  You will show your reading and listening skills on this
                  benchmark.
                </Typography>
                <Typography>Each part has three tasks:</Typography>
                <ul>
                  <li>
                    <Typography>
                      <span style={{ fontWeight: 800 }}>Reading Maze:</span>{" "}
                      Choose the best word to complete the text.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <span style={{ fontWeight: 800 }}>Read Aloud:</span> Read
                      the text out loud.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      <span style={{ fontWeight: 800 }}>Vocabulary: </span>{" "}
                      Choose words with the same or similar meaning.
                    </Typography>
                  </li>
                </ul>
                <Typography>
                  After you finish all three parts, you will record yourself
                  retelling the story.
                </Typography>
                <Typography>Let’s begin.</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
